import React, { useState } from 'react';
import { formatMoney } from '../../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import { Settings2, X, Minus, Plus } from 'lucide-react';
import type { BasketItem, Product } from '../../types/order';
import { hidesSizeName } from '../../utils/sellableSizes';
import NumpadModal from './NumpadModal';
import { formatEuro } from '../../utils/formatEuro';

interface BasketItemRowProps {
  item: BasketItem;
  onRemove: (id: string) => void;
  onQuantityChange: (id: string, delta: number) => void;
  /** Called when user clicks the row (typically to open edit modal). */
  onClick?: (item: BasketItem) => void;
  /** If true, onClick only fires for items with a selectedSize (TakeAway behavior). */
  clickableOnlyWithSize?: boolean;
  /** Resolves the unit price for a weight-variable product. Differs per channel. */
  getUnitPrice: (product: Product) => number;
  /**
   * Which field to multiply by quantity for the displayed line total:
   * - `totalPrice` — TakeAway style
   * - `price` — DeliveryWithMap style (falls back to totalPrice if price missing)
   */
  priceField?: 'totalPrice' | 'price';
  /** Small grey note shown under the product name. Purely informational —
   *  does NOT affect the line price. Used to flag that a product carries a
   *  plastic fee (e.g. "+ τέλος €0.10 / τεμ."), which is charged on its own
   *  separate basket line. */
  infoNote?: string;
  /** «Νέα εμφάνιση»: γραμμή πίνακα (Προϊόν · Ποσότητα · Σύνολο) αντί για κάρτα. */
  look?: 'classic' | 'v2';
}

/** Στήλες της γραμμής στη νέα εμφάνιση — τις ίδιες χρησιμοποιεί και η κεφαλίδα του πίνακα. */
// Κουμπιά 44px (στόχος αφής, 11/10/2026): ποσότητα 44+28+44, σύνολο, × 44.
export const BASKET_V2_COLUMNS = 'grid-cols-[minmax(0,1fr)_7.25rem_4.5rem_2.75rem]';

const BasketItemRow: React.FC<BasketItemRowProps> = ({
  item,
  onRemove,
  onQuantityChange,
  onClick,
  clickableOnlyWithSize = false,
  getUnitPrice,
  priceField = 'totalPrice',
  infoNote = '',
  look = 'classic',
}) => {
  const { t } = useTranslation('common');
  const [quantityNumpadOpen, setQuantityNumpadOpen] = useState(false);
  const clickable = !clickableOnlyWithSize || !!item.selectedSize;
  // Απόφαση 15 / F1-S1 (χωρίς διακόπτη): δεν γράφεται «(μέγεθος)» όταν είναι το ΜΟΝΑΔΙΚΟ
  // πωλήσιμο μέγεθος του προϊόντος (π.χ. «Κανονικό»), αλλιώς ο ταμίας το διαβάζει ως επιλογή.
  // Προϊόν-τέλος (F1-W4-4): ΠΟΤΕ όνομα μεγέθους.
  // Αφορά ΜΟΝΟ την ετικέτα: η γραμμή κρατά το selectedSize, τιμή και payload δεν αλλάζουν, και
  // η κρυμμένη ετικέτα δεν αλλάζει το «clickable» (η γραμμή έχει ακόμη μέγεθος).
  const showSizeName = !!item.selectedSize
    && !hidesSizeName(item.product, item.selectedSize.id);

  // «@ €/kg» της ζυγιζόμενης γραμμής: η τιμή κιλού που έδωσε ο ταμίας («Βάρος + τιμή κιλού»,
  // utils/weightedOpenPrice), αλλιώς η τιμή καταλόγου του καναλιού όπως πάντα.
  const weightUnitPrice = item.weightUnitPrice != null && Number.isFinite(item.weightUnitPrice)
    ? item.weightUnitPrice
    : (item.weight ? getUnitPrice(item.product) : 0);

  const handleRowClick = () => {
    if (clickable && onClick) onClick(item);
  };

  const linePrice = (() => {
    const unit = priceField === 'price'
      ? (typeof item.price === 'number' ? item.price : parseFloat(String(item.price)) || 0)
      : item.totalPrice;
    return unit * item.quantity;
  })();

  const hasModifications =
    (item.removedIngredients && item.removedIngredients.length > 0) ||
    (item.addedExtras && item.addedExtras.length > 0) ||
    !!item.specialInstructions ||
    (item.selected_options && item.selected_options.length > 0);

  const numpad = (
    <NumpadModal
      open={quantityNumpadOpen}
      initialValue={0}
      suffix=""
      title={`Ποσότητα · ${item.product.name}`}
      min={0}
      max={999999}
      onClose={() => setQuantityNumpadOpen(false)}
      onConfirm={quantity => {
        // OK χωρίς αριθμό (ή με 0) = καμία αλλαγή. Με min 1 το κενό γινόταν 1 και μια γραμμή ×5
        // έπεφτε σιωπηλά σε ×1. Για αφαίρεση υπάρχουν το «−» και το «×».
        const nextQuantity = Math.floor(quantity);
        if (nextQuantity >= 1 && nextQuantity !== item.quantity) {
          onQuantityChange(item.id, nextQuantity - item.quantity);
        }
        setQuantityNumpadOpen(false);
      }}
    />
  );

  if (look === 'v2') {
    const unit = item.weightUnit === 'LT' ? 'lt' : 'kg';
    const stepButton = 'cashier-quantity-button w-11 h-11 rounded-xl border border-line dark:border-line-strong text-t2 hover:bg-surface-3 flex items-center justify-center active:scale-95 transition-all';
    return (
      <div
        className={`xv2-basket-row grid ${BASKET_V2_COLUMNS} items-center gap-x-2 py-2.5 border-b border-line ${clickable ? 'cursor-pointer' : ''}`}
        onClick={handleRowClick}
      >
        <div className="min-w-0">
          {/* Χωρίς εικονίδιο «ρυθμίσεων»: πατιέται όλη η γραμμή, όπως και οι κάρτες του καταλόγου. */}
          <h4 className="text-sm font-medium text-t1 leading-snug line-clamp-2 break-words">{item.product.name}</h4>
          {showSizeName && item.selectedSize && (
            <div className="text-xs text-t3 truncate">{item.selectedSize.name}</div>
          )}
          {hasModifications && (
            <div className="text-xs leading-snug mt-0.5 flex flex-wrap gap-x-1.5">
              {item.selected_options && item.selected_options.length > 0 && (
                <span className="text-t3">{item.selected_options.map(o => o.choice_name).join(' · ')}</span>
              )}
              {item.removedIngredients && item.removedIngredients.length > 0 && item.removedIngredientNames && item.removedIngredientNames.length > 0 && (
                <span className="text-red-600 dark:text-red-400">χωρίς {item.removedIngredientNames.join(', ')}</span>
              )}
              {item.addedExtras && item.addedExtras.length > 0 && (
                <span className="text-t2">+ {item.addedExtras.map(extra => extra.name).join(', ')}</span>
              )}
              {item.specialInstructions && (
                <span className="text-t4 italic truncate max-w-[12rem]">"{item.specialInstructions}"</span>
              )}
            </div>
          )}
          {infoNote && <div className="text-[11px] text-t3 mt-0.5">{infoNote}</div>}
        </div>

        {item.weight ? (
          <div className="text-xs leading-tight">
            <div className="font-semibold text-t1 tabular-nums">{item.weight.toFixed(3).replace('.', ',')} {unit}</div>
            <div className="text-t3 tabular-nums">@ {formatEuro(weightUnitPrice)}/{unit}</div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <button
              aria-label={`${t('quantity')} −1: ${item.product.name}`}
              onClick={e => { e.stopPropagation(); onQuantityChange(item.id, -1); }}
              className={stepButton}
            >
              <Minus className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={`${t('quantity')} ${item.product.name}`}
              title="Πάτησε για εισαγωγή ποσότητας"
              onClick={e => { e.stopPropagation(); setQuantityNumpadOpen(true); }}
              className="min-w-[1.75rem] h-11 px-0.5 text-base font-semibold text-t1 tabular-nums rounded-lg hover:bg-surface-3 active:scale-95 transition-all"
            >
              {item.quantity}
            </button>
            <button
              aria-label={`${t('quantity')} +1: ${item.product.name}`}
              onClick={e => { e.stopPropagation(); onQuantityChange(item.id, 1); }}
              className={stepButton}
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="text-right">
          <div className="text-sm font-semibold text-t1 tabular-nums whitespace-nowrap">{formatEuro(linePrice)}</div>
          {item.discount && item.discount > 0 && (
            <div className="text-[11px] text-t3">−{item.discount}%</div>
          )}
        </div>

        <button
          aria-label={`${t('remove')}: ${item.product.name}`}
          onClick={e => { e.stopPropagation(); onRemove(item.id); }}
          className="w-11 h-11 flex items-center justify-center text-t4 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-all active:scale-95"
        >
          <X className="h-4 w-4" />
        </button>
        {numpad}
      </div>
    );
  }

  return (
    <div
      className={`cashier-basket-item bg-surface-1 rounded-lg p-2 border border-line hover:border-line-strong transition-colors ${clickable ? 'cursor-pointer' : ''}`}
      onClick={handleRowClick}
    >
      {/* Row 1: Name, Size, Price, Delete */}
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-sm truncate text-t1">{item.product.name}</h4>
            {showSizeName && item.selectedSize && (
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium shrink-0">({item.selectedSize.name})</span>
            )}
            {clickable && <Settings2 className="h-3 w-3 text-t4" />}
          </div>
          {hasModifications && (
            <div className="flex flex-wrap gap-1 mt-0.5">
              {item.selected_options && item.selected_options.length > 0 && (
                <span className="text-xs text-indigo-600 dark:text-indigo-400">
                  {item.selected_options.map(o => o.choice_name).join(' · ')}
                </span>
              )}
              {item.removedIngredients && item.removedIngredients.length > 0 && item.removedIngredientNames && item.removedIngredientNames.length > 0 && (
                <span className="text-xs text-red-600 dark:text-red-400">-{item.removedIngredientNames.join(', ')}</span>
              )}
              {item.addedExtras && item.addedExtras.length > 0 && (
                <span className="text-xs text-green-600 dark:text-green-400">
                  +{item.addedExtras.map(extra => extra.name).join(', ')}
                </span>
              )}
              {item.specialInstructions && (
                <span className="text-xs text-t4 italic truncate max-w-[100px]">"{item.specialInstructions}"</span>
              )}
            </div>
          )}
          {infoNote && (
            <div className="text-[10px] text-t3 mt-0.5">{infoNote}</div>
          )}
        </div>
        <span className="cashier-line-price font-bold text-sm text-blue-600 dark:text-blue-400 shrink-0">{formatMoney(linePrice)}</span>
        <button
          aria-label={`${t('remove')}: ${item.product.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.id);
          }}
          className="w-7 h-7 flex items-center justify-center text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-all active:scale-95 shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Row 2: Quantity controls or Weight display */}
      <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-line">
        {item.weight ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-amber-700 dark:text-amber-400 font-medium bg-amber-50 dark:bg-amber-900/30 px-2 py-1 rounded-lg">
              {item.weight.toFixed(3)} {item.weightUnit === 'LT' ? 'lt' : 'kg'}
            </span>
            <span className="text-xs text-t4">
              @ {formatMoney(weightUnitPrice)}/{item.weightUnit === 'LT' ? 'lt' : 'kg'}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button
              aria-label={`${t('quantity')} −1: ${item.product.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onQuantityChange(item.id, -1);
              }}
              className="cashier-quantity-button w-8 h-8 rounded-lg bg-surface-2 hover:bg-surface-3 text-t2 flex items-center justify-center active:scale-95 transition-all"
            >
              <Minus className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={`${t('quantity')} ${item.product.name}`}
              title="Πάτησε για εισαγωγή ποσότητας"
              onClick={e => {
                e.stopPropagation();
                setQuantityNumpadOpen(true);
              }}
              className="font-bold text-base w-8 h-8 text-center text-t1 rounded-lg hover:bg-surface-2 active:scale-95 transition-all"
            >
              {item.quantity}
            </button>
            <button
              aria-label={`${t('quantity')} +1: ${item.product.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onQuantityChange(item.id, 1);
              }}
              className="cashier-quantity-button w-8 h-8 rounded-lg bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center active:scale-95 transition-all"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        )}
        {item.discount && item.discount > 0 && (
          <span className="text-xs text-green-600 dark:text-green-400 font-medium">-{item.discount}%</span>
        )}
      </div>
      {numpad}
    </div>
  );
};

export default BasketItemRow;
